/*
 * Copyright (c) 2026 Dobler Data Solutions, LLC
 * Fabric Control Plane - Refresh History List
 * Feature #197: Portal Light-Mode Theme, Modals & Responsive UX
 * Constitution: v1.20.0
 * Conformed to Spec 197 design blueprint
 */

import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, RefreshCw } from 'lucide-react';
import { useRefreshHistoryList } from '../../services/operations/metadataService';
import type { RefreshHistoryListItem } from '../../services/operations/metadataService';
import {
  PageLayout,
  Card,
  DataTable,
  Button,
  EmptyState,
  ErrorState,
  LoadingState,
} from '../../components/ui';
import type { DataTableColumn } from '../../components/ui';
import { formatEastern } from '@/utils/dateFormatter';

function formatDate(iso: string | null): string {
  if (!iso) return '—';
  return formatEastern(iso);
}

const columns: DataTableColumn<RefreshHistoryListItem>[] = [
  { key: 'displayName', header: 'Model', width: '35%' },
  { key: 'workspaceName', header: 'Workspace', width: '25%' },
  { key: 'refreshCount', header: 'Refreshes', width: '15%', align: 'right' },
  {
    key: 'lastRefresh',
    header: 'Last Refresh',
    width: '25%',
    render: (r) => <span>{formatDate(r.lastRefresh)}</span>,
  },
];

export const RefreshHistoryListPage: React.FC = () => {
  const navigate = useNavigate();
  const { data, isLoading, isError } = useRefreshHistoryList();

  return (
    <PageLayout
      helpId="auto.refresh-history-list"
      title="Refresh History"
      description="Semantic models with collected dataset refresh history"
      icon={<RefreshCw className="h-6 w-6" />}
      actions={
        <Button variant="ghost" size="sm" leftIcon={<ArrowLeft className="h-4 w-4" />} onClick={() => navigate('/metadata')}>
          Metadata
        </Button>
      }
    >
      {isLoading ? (
        <LoadingState rows={6} />
      ) : isError ? (
        <ErrorState
          title="Failed to load refresh history"
          message="Unable to retrieve refresh history list. Please try again."
        />
      ) : !data?.length ? (
        <EmptyState
          title="No refresh history collected"
          message="No semantic models with refresh history have been collected yet. Run the daily collection job to populate this list."
        />
      ) : (
        <Card padding="none">
          <DataTable
            columns={columns}
            rows={data}
            rowKey={(r) => r.id}
            onRowClick={(r) => navigate(`/metadata/datasets/${r.id}/refresh`)}
            emptyMessage="No semantic models with refresh history collected yet."
          />
        </Card>
      )}
    </PageLayout>
  );
};
